import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { listCampaignsApi, deleteCampaignApi, updateActiveState } from '../api/api';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import ConfirmationModal from '../components/ConfirmationModal';

const CampaignList = ({ setAlert }) => {
  const history = useHistory();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [editTarget, setEditTarget] = useState('');
  const [state, setState] = useState('')
  const [expandedRows, setExpandedRows] = useState(new Set());

  const handleEdit = (name) => setEditTarget(name);
  const confirmEdit = () => {
    history.push(`/campaigns/${encodeURIComponent(editTarget)}`);
    setEditTarget('');
  };
  const toggleRowExpansion = (campaignName) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(campaignName)) {
      newExpandedRows.delete(campaignName);
    } else {
      newExpandedRows.add(campaignName);
    }
    setExpandedRows(newExpandedRows);
  };

  const renderTemplates = (templates, campaignName) => {
    if (!templates || templates.length === 0) return '';
    
    const isExpanded = expandedRows.has(campaignName);
    const showEllipsis = templates.length > 2 && !isExpanded;
    
    if (showEllipsis) {

      const visibleTemplates = templates.slice(0, 2);
      const remainingCount = templates.length - 2;
      
      return (
        <div className="flex items-center flex-wrap gap-1">
          <span>{visibleTemplates.join(', ')}</span>
          <button
            onClick={() => toggleRowExpansion(campaignName)}
            className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium ml-1"
            title={`Show ${remainingCount} more template${remainingCount > 1 ? 's' : ''}`}
          >
            ... (+{remainingCount} more)
          </button>
        </div>
      );
    } else if (isExpanded) {

      return (
        <div className="flex items-center flex-wrap gap-1">
          <span>{templates.join(', ')}</span>
          <button
            onClick={() => toggleRowExpansion(campaignName)}
            className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium ml-1"
          >
            (show less)
          </button>
        </div>
      );
    } else {

      return templates.join(', ');
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await listCampaignsApi();
        setCampaigns(res.campaigns || []);
      } catch (err) {
        console.error('Failed to load campaigns:', err);
        setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to load campaigns.' });
      } finally {
        setLoading(false);
      }
    };
    setTimeout(() => load(), 1000);
  }, [setAlert]);

  const handleDelete = (name) => {
    setDeleteInfo(name);
  };
  console.log("CAMPS: ", campaigns)

  const confirmDelete = async () => {
    const name = deleteInfo;
    try {
      const res = await deleteCampaignApi(name);
      setCampaigns((c) => c.filter((cam) => cam.campaignName !== name));

      const templatesMsg =
        res?.deletedCampaign?.templates && res.deletedCampaign.templates.length > 0
          ? ` Templates removed: ${res.deletedCampaign.templates.join(', ')}.`
          : '';

      setAlert?.({
        variant: 'success',
        heading: 'Campaign Deleted',
        text: `${res?.message || 'Campaign deleted successfully.'}${templatesMsg}`,
      });
    } catch (err) {
      console.error('Delete failed', err);
      const msg = err?.message || 'Failed to delete campaign.';
      setAlert?.({ variant: 'error', heading: 'Error', text: msg });
    } finally {
      setDeleteInfo(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
          <p className="text-gray-600 mt-1">Manage campaigns</p>
        </div>
        <button onClick={() => history.push('/campaigns/new')} className="btn-outline">New Campaign</button>
      </div>
      <div className="card">
        {loading ? (
          <div className="text-center text-gray-500">Loading...</div>
        ) : campaigns.length === 0 ? (
          <p className="text-center text-gray-500">No campaigns found.</p>
        ) : (
          <div className="overflow-x-auto space-y-4">
            <input
              type="text"
              className="input-field mb-2"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Templates</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campaigns
                  .filter(c => c.campaignName.toLowerCase().includes(search.toLowerCase()))
                  .map(c => (
                  <tr key={c.campaignName} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{c.campaignName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{renderTemplates(c.templates, c.campaignName)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm ">
                      {c.active == false && (
                        <>
                          <button
                            onClick={() => {updateActiveState(c.campaignName); setAlert({ variant: 'success', heading: `${c.campaignName} Reactivated`, text: 'Campaign reactivated' })}}
                            className="btn-outline mr-2"
                            style={{backgroundColor: 'green', color: 'white'}}
                          >
                            Re-activate
                          </button> 
                          <button
                            onClick={() => handleDelete(c.campaignName)}
                            className="btn-outline mr-2 text-red-600 border-red-300 hover:bg-red-50"
                            style={{backgroundColor: 'red', color: 'white'}}
                          >
                            Delete
                          </button>                           
                        </>
                      )}
                      <button
                        onClick={() => history.push(`/campaigns/view/${encodeURIComponent(c.campaignName)}`)}
                        className="btn-outline mr-2"
                      >
                        View
                      </button>
                      {(c.active === true || c.active == undefined) && (  
                        <>
                        <button
                          onClick={() => handleEdit(c.campaignName)}
                          className="btn-outline mr-2"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {updateActiveState(c.campaignName); setAlert({ variant: 'success', heading: `${c.campaignName} Deactivated`, text: 'Campaign has been deactivated.' })}}
                          className="btn-outline mr-2"
                          style={{backgroundColor: 'orange'}}
                        >
                          Inactivate
                        </button>                          
                        </>                    
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ConfirmationModal
        open={!!editTarget}
        title="Edit Confirmation"
        message={`Are you sure you want to enter edit mode for ${editTarget}?`}
        confirmText="Continue"
        onClose={() => setEditTarget('')}
        onConfirm={confirmEdit}
      />
      <DeleteConfirmationModal
        open={!!deleteInfo}
        itemName={deleteInfo || ''}
        onClose={() => setDeleteInfo(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default CampaignList;
